import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MonitorSpeaker, MapPin, ChevronLeft, Building2 } from "lucide-react";
import {
  useGetSession,
  useListOperableDevices,
  getListOperableDevicesQueryKey,
  type OperableDevice,
} from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/ui/error-state";
import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  useWorkspaceOptional,
} from "@/hooks/use-workspace";

const NO_LOCATION = "__none__";

function SelectorLayout({
  embedded,
  children,
}: {
  embedded: boolean;
  children: React.ReactNode;
}) {
  if (embedded) {
    return <div className="w-full max-w-md mx-auto">{children}</div>;
  }
  return (
    <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

function Spinner({ embedded = false }: { embedded?: boolean }) {
  return (
    <SelectorLayout embedded={embedded}>
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    </SelectorLayout>
  );
}

/**
 * Shared workspace -> location -> device selection flow used by both the
 * on-screen device picker (which claims a durable credential) and the phone
 * handoff (which activates a waiting screen session). Single options are
 * auto-skipped: one workspace skips the workspace step, devices in a single
 * location skip the location step, and a lone device is auto-selected.
 */
function statusLabel(status: OperableDevice["status"]): string {
  switch (status) {
    case "online":
      return t("common.status.online");
    case "idle":
      return t("playerUi.selector.deviceStatus.idle");
    case "error":
      return t("playerUi.selector.deviceStatus.error");
    default:
      return t("common.status.offline");
  }
}

export default function DeviceSelector({
  onSelect,
  pending,
  actionLabel,
  title,
  subtitle,
  embedded = false,
  showStatus = false,
  pinDeviceId = null,
  excludeDeviceId = null,
}: {
  onSelect: (workspaceId: string, deviceId: string) => void;
  pending: boolean;
  actionLabel: string;
  title: string;
  subtitle: string;
  embedded?: boolean;
  showStatus?: boolean;
  pinDeviceId?: string | null;
  excludeDeviceId?: string | null;
}) {
  const {
    data: session,
    isLoading: sessionLoading,
    isError: sessionError,
    refetch: refetchSession,
    isRefetching: sessionRefetching,
  } = useGetSession();
  const workspaces = session?.workspaces ?? [];
  const workspaceCtx = useWorkspaceOptional();
  const preferredWorkspaceId =
    workspaceCtx?.activeWorkspaceId ??
    (typeof localStorage !== "undefined"
      ? localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
      : null);

  const [workspaceId, setWorkspaceId] = useState<string | null>(() => {
    if (!embedded || typeof localStorage === "undefined") return null;
    return localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY);
  });
  const [locationKey, setLocationKey] = useState<string | null>(null);

  // Reuse workspace already chosen on /workspaces (hub → control|player-selection).
  useEffect(() => {
    if (!preferredWorkspaceId) return;
    const valid = workspaces.some((w) => w.id === preferredWorkspaceId);
    if (!valid) return;
    if (workspaceId !== preferredWorkspaceId) {
      setWorkspaceId(preferredWorkspaceId);
      setLocationKey(null);
    }
  }, [preferredWorkspaceId, workspaces, workspaceId]);

  // Auto-select the only workspace when none is active yet.
  useEffect(() => {
    if (!workspaceId && workspaces.length === 1) {
      setWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, workspaceId]);

  const {
    data: devices = [],
    isLoading: devicesLoading,
    isError: devicesError,
    refetch: refetchDevices,
    isRefetching: devicesRefetching,
  } = useListOperableDevices(workspaceId ?? "", {
    query: {
      enabled: Boolean(workspaceId),
      queryKey: getListOperableDevicesQueryKey(workspaceId ?? ""),
    },
  });

  const locations = useMemo(() => {
    const map = new Map<string, { key: string; name: string }>();
    for (const d of devices) {
      const key = d.locationId ?? NO_LOCATION;
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: d.locationName ?? t("playerUi.selector.unassignedLocation"),
        });
      }
    }
    return Array.from(map.values());
  }, [devices]);

  // Auto-select the only location once devices are loaded.
  useEffect(() => {
    if (workspaceId && !locationKey && locations.length === 1) {
      setLocationKey(locations[0].key);
    }
  }, [workspaceId, locationKey, locations]);

  const scopedDevices = useMemo(() => {
    let list = locationKey
      ? devices.filter((d) => (d.locationId ?? NO_LOCATION) === locationKey)
      : [];
    if (excludeDeviceId) {
      list = list.filter((d) => d.id !== excludeDeviceId);
    }
    if (!pinDeviceId) return list;
    const pinned = list.find((d) => d.id === pinDeviceId);
    const rest = list.filter((d) => d.id !== pinDeviceId);
    return pinned ? [pinned, ...rest] : list;
  }, [devices, locationKey, pinDeviceId, excludeDeviceId]);

  // Auto-select a lone device (one-shot guard so the mutation fires once).
  // Skipped in embedded mode so the player-selection page stays visible for
  // explicit confirmation.
  const autoFired = useRef(false);
  useEffect(() => {
    if (embedded) return;
    if (
      workspaceId &&
      locationKey &&
      !devicesLoading &&
      scopedDevices.length === 1 &&
      !autoFired.current &&
      !pending
    ) {
      autoFired.current = true;
      onSelect(workspaceId, scopedDevices[0].id);
    }
  }, [embedded, workspaceId, locationKey, devicesLoading, scopedDevices, onSelect, pending]);

  if (sessionLoading) return <Spinner embedded={embedded} />;

  if (sessionError) {
    return (
      <SelectorLayout embedded={embedded}>
        <ErrorState
          title={t("playerUi.selector.accountError.title")}
          description={t("playerUi.selector.accountError.description")}
          onRetry={() => void refetchSession()}
          isRetrying={sessionRefetching}
        />
      </SelectorLayout>
    );
  }

  if (workspaces.length === 0) {
    return (
      <SelectorLayout embedded={embedded}>
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("playerUi.selector.noWorkspaces.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("playerUi.selector.noWorkspaces.description")}
          </p>
        </div>
      </SelectorLayout>
    );
  }

  const header = (
    <div className="flex flex-col items-center text-center mb-6">
      <div className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
        <MonitorSpeaker className="size-7" />
      </div>
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">{subtitle}</p>
    </div>
  );

  // Step 1: workspace selection.
  if (!workspaceId) {
    return (
      <SelectorLayout embedded={embedded}>
        {header}
        <div className="rounded-xl border bg-card p-2 shadow-sm space-y-1">
          {workspaces.map((w) => (
            <button
              key={w.id}
              onClick={() => setWorkspaceId(w.id)}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-accent transition-colors"
              data-testid={`button-workspace-${w.id}`}
            >
              <Building2 className="size-4 text-muted-foreground" />
              <span className="font-medium">{w.name}</span>
            </button>
          ))}
        </div>
      </SelectorLayout>
    );
  }

  if (devicesLoading) return <Spinner embedded={embedded} />;

  if (devicesError) {
    return (
      <SelectorLayout embedded={embedded}>
        {header}
        {workspaces.length > 1 && !embedded && (
          <BackButton
            onClick={() => setWorkspaceId(null)}
            label={t("playerUi.selector.back.workspaces")}
          />
        )}
        <ErrorState
          title={t("playerUi.selector.devicesError.title")}
          description={t("playerUi.selector.devicesError.description")}
          onRetry={() => void refetchDevices()}
          isRetrying={devicesRefetching}
        />
      </SelectorLayout>
    );
  }

  // Step 2: location selection.
  if (!locationKey) {
    return (
      <SelectorLayout embedded={embedded}>
        {header}
        {workspaces.length > 1 && !embedded && (
          <BackButton
            onClick={() => setWorkspaceId(null)}
            label={t("playerUi.selector.back.workspaces")}
          />
        )}
        {locations.length === 0 ? (
          <EmptyDevices />
        ) : (
          <div className="rounded-xl border bg-card p-2 shadow-sm space-y-1">
            {locations.map((loc) => (
              <button
                key={loc.key}
                onClick={() => setLocationKey(loc.key)}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-accent transition-colors"
                data-testid={`button-location-${loc.key}`}
              >
                <MapPin className="size-4 text-muted-foreground" />
                <span className="font-medium">{loc.name}</span>
              </button>
            ))}
          </div>
        )}
      </SelectorLayout>
    );
  }

  // Step 3: device selection.
  return (
    <SelectorLayout embedded={embedded}>
      {header}
      {locations.length > 1 && (
        <BackButton
          onClick={() => setLocationKey(null)}
          label={t("playerUi.selector.back.locations")}
        />
      )}
      {scopedDevices.length === 0 ? (
        <EmptyDevices />
      ) : (
        <div className="rounded-xl border bg-card p-2 shadow-sm space-y-1">
          {scopedDevices.map((d: OperableDevice) => (
            <button
              key={d.id}
              onClick={() => onSelect(workspaceId, d.id)}
              disabled={pending}
              className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-3 text-left hover:bg-accent transition-colors disabled:opacity-60"
              data-testid={`button-device-${d.id}`}
            >
              <span className="flex items-center gap-3">
                <MonitorSpeaker className="size-4 text-muted-foreground" />
                <span>
                  <span className="font-medium block">
                    {d.id === pinDeviceId
                      ? t("playerUi.selector.thisDevice", { name: d.name })
                      : d.name}
                  </span>
                  {d.locationName && (
                    <span className="text-xs text-muted-foreground">
                      {d.locationName}
                    </span>
                  )}
                </span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {showStatus ? (
                  <span className="text-xs text-muted-foreground">
                    {statusLabel(d.status)}
                  </span>
                ) : null}
                {pending ? (
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                ) : (
                  <span className="text-xs font-medium text-primary">
                    {actionLabel}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </SelectorLayout>
  );
}

function BackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="mb-3 -ml-2"
      data-testid="button-selector-back"
    >
      <ChevronLeft className="size-4" />
      {label}
    </Button>
  );
}

function EmptyDevices() {
  return (
    <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
      {t("playerUi.selector.empty")}
    </div>
  );
}
